import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API, api, setToken, getToken } from "./api";
import { clearAppCache } from "./pwa";

const AuthCtx = createContext(null);

// Google sign-in (self-hosted OAuth on the API: GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET).
// Build with REACT_APP_GOOGLE_AUTH=on to show the button once the API has Google configured.
export const googleAuth = {
  enabled: process.env.REACT_APP_GOOGLE_AUTH === "on",
  start() {
    window.location.href = `${API}/auth/google/start`;
  },
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, agency: null, loading: true, error: false });

  // Only a 401/403 means the session is gone. A network error or a 5xx (e.g. the API waking up) is retried, then shown as
  // a load error with "Try again" instead of a blank page or a surprise logout.
  const refresh = useCallback(async () => {
    if (!getToken()) return setState({ user: null, agency: null, loading: false, error: false });
    setState((s) => (s.error ? { ...s, loading: true, error: false } : s));
    for (let attempt = 0; ; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setState({ user: data.user, agency: data.agency, loading: false, error: false });
        return data;
      } catch (e) {
        const st = e?.response?.status;
        if (st === 401 || st === 403) {
          setToken(null);
          return setState({ user: null, agency: null, loading: false, error: false });
        }
        // Offline (installed app opened without signal): say so straight away; it reloads itself when the connection is back.
        if (attempt >= 3 || (typeof navigator !== "undefined" && navigator.onLine === false)) return setState((s) => ({ ...s, loading: false, error: true }));
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
  }, []);

  useEffect(() => {
    if (window.location.hash.includes("session_id=")) return setState((s) => ({ ...s, loading: false }));
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state.error) return undefined;
    const on = () => refresh();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [state.error, refresh]);

  const signIn = async (token) => {
    setToken(token);
    return refresh();
  };
  const signOut = () => {
    // revoke this token server-side (best effort), then forget it locally
    const tk = getToken();
    if (tk) api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${tk}` } }).catch(() => {});
    setToken(null);
    clearAppCache();
    setState({ user: null, agency: null, loading: false, error: false });
  };
  const setAgency = (agency) => setState((s) => ({ ...s, agency }));

  return <AuthCtx.Provider value={{ ...state, refresh, signIn, signOut, setAgency }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const canManage = (user) => ["owner", "admin"].includes(user?.role);
